var LoopSubscriptions = (function () {
  const LOOP_APP_ID = "5284869";
  const LOOP_WIDGET_USE_COMPARE_AT_PRICE = false;
  const LOOP_WIDGET_VARIANT_ID_SELECTORS = ['input[name="id"]'];
  const LOOP_WIDGET_SELLING_PLAN_SELECTOR = 'input[name="selling_plan"]';
  const LOOP_CDN_URL = "https://cdn.loopwork.co";
  const LOOP_WIDGET_PRODUCT_QUANTITY_SELECTORS = [".quantity__input"];
  const OBSERVER_ATTRIBUTE_NAMES = ["value", "variant"];

  async function startLoopWidget(productId) {
    try {
      console.time("renderLoopWidget");
      widgetLogger("product info", window.LOOP_WIDGET);
      observeLoopWidgetVariantChange(productId);
      generateLoopWidgetVariantSPMaps(productId);
      await fetchWidgetData(productId);
      await getLoopWidgetPrepaidSellingPlans(productId);
      renderLoopWidgetContainer(productId);
      loopWidgetProcessBundleProduct(productId);
      setSvgDimensions(productId);
      updateLoopWidgetDropdownArrowSVG(productId);
      selectLoopWidgetPurchaseOption(productId);
      handleLoopWidgetSkeletonLoader(productId);
      listenLoopWidgetAddToCartCustomEvent(productId);
    } catch (error) {
      widgetLogger(
        `Error loading Loop subscriptions widget: ${error?.message}`,
        error
      );
      hideLoopWidget(productId, "startLoopWidget");
      hideLoopWidgetLoader(productId);
    } finally {
      console.timeEnd("renderLoopWidget");
    }
  }

  function hideLoopWidgetLoader(productId) {
    const skeletonLoader = document.getElementById(
      `loop-widget-skeleton-container-id-${productId}`
    );
    if (skeletonLoader) {
      skeletonLoader.classList.add("loop-display-none");
    }
  }

  function hideLoopWidget(productId, sourceFunction, message = "") {
    widgetLogger("Hiding widget from: ", sourceFunction, message);
    const widgetContainer = document.getElementById(
      `loop-widget-container-id-${productId}`
    );
    if (widgetContainer) {
      widgetContainer.classList.add("loop-display-none");
    }
  }

  function showLoopWidget(productId) {
    document
      .getElementById(`loop-widget-container-id-${productId}`)
      .classList.remove("loop-display-none");
  }

  function showLoopWidgetLoader(productId) {
    document
      .getElementById(`loop-widget-skeleton-container-id-${productId}`)
      .classList.remove("loop-display-none");
  }

  function handleLoopWidgetSkeletonLoader(productId) {
    hideLoopWidgetLoader(productId);
    const variantSellingPlanGroups = getVariantSellingPlanGroups(
      productId,
      getLoopWidgetVariantId(productId)
    );
    if (variantSellingPlanGroups.length) {
      dispatchLoopWidgetEvent(productId, "loopWidgetLoaded", {});
      showLoopWidget(productId);
    } else {
      hideLoopWidget(
        productId,
        "handleLoopWidgetSkeletonLoader",
        "No selling plan groups found"
      );
      hideLoopWidgetLoader(productId);
    }
    if (
      getLoopWidgetPreferenceByKey(productId, "alwaysShowSellingPlanDetails")
    ) {
      document.documentElement.style.setProperty(
        "--loop-widget-always-show-details-height",
        "500px"
      );
    }
  }

  async function getLoopWidgetPrepaidSellingPlans(productId) {
    if (!window.LOOP_WIDGET[productId].storeJson.hasPrepaid) {
      return;
    }
    const sps =
      window.LOOP_WIDGET[productId].product.selling_plan_groups?.flatMap(
        (spg) => {
          return spg?.selling_plans.map((sp) => sp.id);
        }
      ) || [];

    const spIdsAsString = sps.join(",");

    const prepaidUrl = `${window.LOOP_WIDGET[productId].storeJson.apiUrl.prepaidSellingPlans}?shopifyIds=${spIdsAsString}`;
    const prepaidRes = await fetch(prepaidUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const prepaidResJSON = await prepaidRes.json();
    const prepaidData = prepaidResJSON.data;
    window.LOOP_WIDGET[productId]["prepaidSellingPlans"] =
      prepaidData?.sellingPlans || {};
  }

  function getLoopWidgetPrepaidSellingPlanDeliveryFreq(
    productId,
    sellingPlanId
  ) {
    const prepaidData = window.LOOP_WIDGET[productId]["prepaidSellingPlans"];
    const sellingPlanPrepaidInfo = prepaidData[sellingPlanId];
    return {
      deliveryFreq: sellingPlanPrepaidInfo?.deliveriesPerBillingCycle ?? 1,
      isPrepaid: sellingPlanPrepaidInfo?.isPrepaidV2,
    };
  }

  function widgetLogger(message, ...additionalData) {
    const prefix = "%cLoop Widget: ";
    let style = `background-color: #7D41FF; color: #FFFFFF; padding: 2px;`;
    console.log(prefix + message, style, ...additionalData);
  }

  async function fetchWidgetData(productId) {
    const myShopifyDomain = window.Shopify.shop;
    const baseUrl = `${LOOP_CDN_URL}/${myShopifyDomain}`;
    const themeId = window?.Shopify?.theme?.id;
    const storesRes = await fetch(`${baseUrl}/store.json`);
    if (!storesRes) {
      throw new Error("Cannot connect to Loop widget CDN");
    }
    const storeJson = await storesRes.json();
    if (!storeJson) {
      throw new Error("Cannot fetch store data");
    }
    window.LOOP_WIDGET[productId].storeJson = { ...storeJson };
    if (!storeJson.isStorefrontWidgetPublished) {
      throw new Error("Widget is not published");
    }
    const templateName =
      window.LOOP_WIDGET[productId].templateName || "default";
    const widgetId = storeJson?.widgetMapping?.[themeId]?.[templateName];
    if (!widgetId) {
      throw new Error(`No widget found for template: ${templateName}`);
    }
    const textCdnUrl = `${baseUrl}/widgets/${widgetId}/texts.json`;
    const preferencesCdnUrl = `${baseUrl}/widgets/${widgetId}/preferences.json`;
    const stylesCdnUrl = `${baseUrl}/widgets/${widgetId}/styles.css`;

    const [textResponse, preferencesResponse, stylesResponse] =
      await Promise.all([
        fetch(textCdnUrl),
        fetch(preferencesCdnUrl),
        fetch(stylesCdnUrl),
      ]);

    const [texts, preferences, styles] = await Promise.all([
      textResponse.json(),
      preferencesResponse.json(),
      stylesResponse.text(),
    ]);

    window.LOOP_WIDGET[productId].texts = texts;
    window.LOOP_WIDGET[productId].preferences = preferences;
    const styleElement = document.createElement("style");
    styleElement.textContent = styles;
    styleElement.id = `loop-widget-styles-id-${productId}`;
    document.body.appendChild(styleElement);

    // fetch preset bundle info
    if (
      storeJson.hasPresetBundles &&
      storeJson.presetBundleShopifyProductIds.includes(productId)
    ) {
      const presetUrl = `${baseUrl}/presetBundles/${productId}.json`;
      const presetRes = await fetch(presetUrl);
      const productBundleData = await presetRes.json();
      window.LOOP_WIDGET[productId]["productBundleData"] = {
        ...productBundleData,
      };
    }
  }

  function getLoopWidgetPreferenceByKey(productId, key) {
    const item = window.LOOP_WIDGET[productId].preferences.find(
      (itm) => itm.key === key
    );
    return item?.value;
  }

  function getLoopWidgetTextsByKey(productId, key) {
    const item = window.LOOP_WIDGET[productId].texts.find(
      (itm) => itm.key === key
    );
    return item?.value ?? "";
  }

  function getLoopWidgetVariantIdFromURL() {
    const url = new URL(document.URL);
    return url.searchParams.get("variant") || "";
  }

  function getLoopWidgetFirstAvailableVariantId(productId) {
    const variant = window.LOOP_WIDGET[productId].product.variants.find(
      (v) => v.available
    );
    return variant
      ? variant.id
      : window.LOOP_WIDGET[productId].product.variants[0].id;
  }

  function getLoopWidgetVariantId(productId) {
    return (
      window.LOOP_WIDGET[productId]["selectedVariantId"] ||
      getLoopWidgetVariantIdFromURL() ||
      getLoopWidgetFirstAvailableVariantId(productId)
    );
  }

  function getLoopWidgetVariantById(productId, variantId) {
    return window.LOOP_WIDGET[productId]["product"].variants.find(
      (variant) => variant.id == variantId
    );
  }

  function getLoopWidgetProductForms(productId, origin = document) {
    if (window.LOOP_WIDGET[productId]["loopWidgetProductForms"]?.length) {
      return window.LOOP_WIDGET[productId]["loopWidgetProductForms"];
    }
    const variantIds = window.LOOP_WIDGET[productId].product.variants.map(
      (v) => v.id
    );
    const formSelectors = [
      `form[id*="product-form-template"][action*="/cart/add"][data-type="add-to-cart-form"]`,
      `form[id*="product-form-template"][action*="/cart/add"]`,
      `form[id*="product-form-template"][data-type="add-to-cart-form"]`,
      `form[action*="/cart/add"][data-type="add-to-cart-form"]`,
      `form[action*="/cart/add"]`,
      `form[data-type="add-to-cart-form"]`,
    ];
    const excludeKeywords = ["installment", "installation"];

    let allForms = [];
    let excludeKeywordForms = [];
    let productIDFilteredForms = [];
    let variantIDFilteredForms = [];
    let variantIDElementFilteredForms = [];

    for (const selector of formSelectors) {
      allForms = origin.querySelectorAll(selector);
      if (allForms.length > 0) {
        break;
      }
    }

    excludeKeywordForms = Array.from(allForms).filter((form) => {
      return !Array.from(form.attributes).some((attr) =>
        excludeKeywords.some((keyword) =>
          attr.value.toLowerCase().includes(keyword)
        )
      );
    });

    productIDFilteredForms = Array.from(excludeKeywordForms).filter((form) =>
      form.innerHTML.includes(productId)
    );
    variantIDFilteredForms = productIDFilteredForms.filter((form) =>
      variantIds.some((variantId) => form.innerHTML.includes(variantId))
    );
    // variantIDElementFilteredForms = variantIDFilteredForms.filter(form => {
    //     let variantInput = null;
    //     for (const key of LOOP_WIDGET_VARIANT_ID_SELECTORS) {
    //         if (!variantInput) {
    //             variantInput = form.querySelector(key);
    //         } else {
    //             break;
    //         }
    //     }
    //     if (variantInput && variantIds.includes(Number(variantInput.value))) {
    //         return true
    //     } else {
    //         return false;
    //     }
    // });

    if (variantIDElementFilteredForms.length > 0) {
      window.LOOP_WIDGET[productId]["loopWidgetProductForms"] =
        variantIDElementFilteredForms;
      return variantIDElementFilteredForms;
    } else if (variantIDFilteredForms.length > 0) {
      window.LOOP_WIDGET[productId]["loopWidgetProductForms"] =
        variantIDFilteredForms;
      return variantIDFilteredForms;
    } else if (productIDFilteredForms.length > 0) {
      window.LOOP_WIDGET[productId]["loopWidgetProductForms"] =
        productIDFilteredForms;
      return productIDFilteredForms;
    } else if (excludeKeywordForms.length > 0) {
      window.LOOP_WIDGET[productId]["loopWidgetProductForms"] =
        excludeKeywordForms;
      return excludeKeywordForms;
    } else {
      window.LOOP_WIDGET[productId]["loopWidgetProductForms"] = allForms;
      return allForms;
    }
  }

  function getTargetAttributeValue(target, attributeName) {
    if (target && target.getAttribute(attributeName)) {
      return target.getAttribute(attributeName);
    }
    return null;
  }

  function observeLoopWidgetFormChangeForVariant(
    productId,
    variantIds,
    relevantForms
  ) {
    relevantForms.forEach((form) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            OBSERVER_ATTRIBUTE_NAMES.includes(mutation.attributeName)
          ) {
            let newValue = getTargetAttributeValue(
              mutation.target,
              mutation.attributeName
            );
            if (newValue && variantIds.includes(Number(newValue))) {
              handleLoopWidgetVariantIdChange(productId, newValue);
            }
          }
        });
      });

      const config = {
        attributes: true,
        childList: false,
        subtree: true,
        attributeFilter: OBSERVER_ATTRIBUTE_NAMES,
      };
      observer.observe(form, config);
    });
  }

  function handleLoopWidgetVariantIdChange(productId, variantId) {
    widgetLogger("VariantId changed: ", variantId);
    window.LOOP_WIDGET[productId]["selectedVariantId"] = Number(variantId);
    renderLoopWidgetContainer(productId);
    selectLoopWidgetPurchaseOption(productId);
  }

  function observeLoopWidgetVariantChange(productId) {
    const variantIds = window.LOOP_WIDGET[productId].product.variants.map(
      (v) => v.id
    );
    const relevantForms = getLoopWidgetProductForms(productId, document);
    if (relevantForms.length === 1) {
      let variantInput = null;
      for (const key of LOOP_WIDGET_VARIANT_ID_SELECTORS) {
        if (!variantInput) {
          variantInput = relevantForms[0].querySelector(key);
        } else {
          break;
        }
      }
      if (variantInput && variantIds.includes(Number(variantInput.value))) {
        window.LOOP_WIDGET[productId]["selectedVariantId"] = Number(
          variantInput.value
        );
      }
    }
    observeLoopWidgetFormChangeForVariant(productId, variantIds, relevantForms);
  }

  function renderLoopWidgetContainer(productId) {
    const loopWidgetContainer = document.getElementById(
      `loop-widget-container-id-${productId}`
    );
    const variantId = getLoopWidgetVariantId(productId);
    loopWidgetContainer.innerHTML = generateLoopWidget(productId, variantId);
  }

  function getPreviousSpgSelectedForVariant(
    productId,
    variantSellingPlanGroups = []
  ) {
    const spgIds = variantSellingPlanGroups.map((spg) => spg.id);
    if (
      window.LOOP_WIDGET[productId].selectedSellingPlanGroupId &&
      spgIds.includes(window.LOOP_WIDGET[productId].selectedSellingPlanGroupId)
    ) {
      return window.LOOP_WIDGET[productId].selectedSellingPlanGroupId;
    } else if (window.LOOP_WIDGET[productId].selectedSellingPlanGroupId) {
      return spgIds[0];
    }
    return null;
  }

  function selectLoopWidgetPurchaseOption(productId) {
    const variantSellingPlanGroups = getVariantSellingPlanGroups(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const spgIdToSelect = getPreviousSpgSelectedForVariant(
      productId,
      variantSellingPlanGroups
    );
    const requires_selling_plan =
      window.LOOP_WIDGET[productId].product.requires_selling_plan;
    if (requires_selling_plan) {
      hideOneTimePurchaseOptionLoopWidget(productId);
    }

    if (
      (getLoopWidgetPreferenceByKey(productId, "purchaseOptionLabel") ===
        "Subscription" &&
        variantSellingPlanGroups.length) ||
      spgIdToSelect ||
      getLoopWidgetProductBundleData(productId).purchaseType ===
        "SUBSCRIPTION" ||
      requires_selling_plan
    ) {
      handleLoopWidgetSellingPlanGroupChange(
        productId,
        spgIdToSelect ?? variantSellingPlanGroups[0]?.id
      );
    } else {
      handleLoopWidgetOnetimeClick(productId);
    }
  }

  function getLoopWidgetSelectedSellingPlanGroupId(productId) {
    let id = window.LOOP_WIDGET[productId]["selectedSellingPlanGroupId"];
    if (
      !id &&
      getLoopWidgetPreferenceByKey(productId, "purchaseOptionLabel") ===
        "Subscription"
    ) {
      const variantSellingPlanGroups =
        window.LOOP_WIDGET[productId].variantToSellingPlanGroups[
          getLoopWidgetVariantId(productId)
        ];
      id = window.LOOP_WIDGET[productId]["product"]["selling_plan_groups"].find(
        (spg) => spg.id === variantSellingPlanGroups[0]
      );
    }
    return id;
  }

  function loopWidgetFormatPrice(value) {
    const { locale, country } = window.Shopify;
    const { active: currency } = window.Shopify.currency;
    const decimalValue = value / 100;
    if (locale?.includes("-")) {
      return new Intl.NumberFormat(`${locale}`, {
        style: "currency",
        currency,
      }).format(decimalValue);
    } else {
      return new Intl.NumberFormat(`${locale}-${country}`, {
        style: "currency",
        currency,
      }).format(decimalValue);
    }
  }

  function getLoopWidgetDiscount(productId, sellingPlan, variant) {
    const storeJson = window.LOOP_WIDGET[productId]["storeJson"];
    const { value_type, value } = sellingPlan.price_adjustments[0];
    const variantPrice = LOOP_WIDGET_USE_COMPARE_AT_PRICE
      ? variant.compare_at_price || variant.price
      : variant.price;
    const { deliveryFreq, isPrepaid } =
      getLoopWidgetPrepaidSellingPlanDeliveryFreq(productId, sellingPlan.id);
    let discount = 0;
    let discountText = "";

    if (storeJson?.presetBundleShopifyProductIds?.includes(productId)) {
      const bundlePrice =
        getLoopWidgetBundlePriceBySellingPlanId(
          productId,
          sellingPlan.id,
          false
        ) || variantPrice;
      discount = Math.round(
        ((variantPrice - bundlePrice) / variantPrice) * 100
      );
      discountText = `${discount}%`;
      return { discount, discountText };
    }

    if (value_type === "price") {
      if (isPrepaid) {
        discount = ((variantPrice - value / deliveryFreq) / variantPrice) * 100;
        discount = Math.round(discount);
        discountText = `${discount}%`;
      } else {
        discount = variantPrice - value;
        discountText = `${loopWidgetFormatPrice(discount)}`;
      }
    } else if (value_type === "percentage") {
      discount = value;
      discountText = `${discount}%`;
    } else if (value_type === "fixed_amount") {
      discount = value;
      discountText = `${loopWidgetFormatPrice(discount)}`;
    }

    return { discount, discountText };
  }

  function handleLoopWidgetOnetimeClick(productId) {
    if (
      !getLoopWidgetPreferenceByKey(productId, "alwaysShowSellingPlanDetails")
    ) {
      document
        .querySelectorAll(`#loop-widget-container-id-${productId} .expanded`)
        .forEach((ele) => ele.classList.remove("expanded"));
    }
    uncheckLoopWidgetSellingPlans(productId);
    removeLoopWidgetSpgSelected(productId);
    handleLoopWidgetSellingPlanValue(productId, null, false);
    loopWidgetUpdateAddToCartButtonText(productId);
    const radio = document.getElementById(
      `loop-widget-onetime-purchase-option-radio-id-${productId}`
    );
    if (radio) {
      radio.innerHTML = getLoopWidgetSvgRadio();
    }
    const onetimeOption = document
      .getElementById(`loop-widget-container-id-${productId}`)
      ?.querySelector(`.loop-widget-purchase-option-onetime`);
    if (onetimeOption) {
      onetimeOption.classList.add("loop-widget-purchase-option-selected");
    }
    window.LOOP_WIDGET[productId]["selectedSellingPlanGroupId"] = null;
  }

  function handleLoopWidgetSellingPlanValue(
    productId,
    selectedSellingPlanId,
    toAdd
  ) {
    const forms = getLoopWidgetProductForms(productId, document);
    if (!forms.length) return;

    window.LOOP_WIDGET[productId]["selectedSellingPlanId"] =
      selectedSellingPlanId;
    forms.forEach((form) => {
      let sellingPlanInput = form.querySelector(
        LOOP_WIDGET_SELLING_PLAN_SELECTOR
      );
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement("input");
        sellingPlanInput.type = "hidden";
        sellingPlanInput.name = "selling_plan";
        form.appendChild(sellingPlanInput);
      }
      if (toAdd) {
        sellingPlanInput.value = selectedSellingPlanId;
      } else if (sellingPlanInput) {
        sellingPlanInput.value = "";
      }
    });
  }

  function removeLoopWidgetSpgSelected(productId) {
    document
      .getElementById(`loop-widget-container-id-${productId}`)
      .querySelectorAll(".loop-widget-purchase-option")
      .forEach((ele) =>
        ele.classList.remove("loop-widget-purchase-option-selected")
      );
  }

  function updateSelectSVG() {
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue("--loop-widget-svg-stroke-color")
      .trim();
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 24 24' fill='none'><path d='M17 9.5L12 14.5L7 9.5' stroke='${color}' stroke-linecap='round' stroke-linejoin='round'/></svg>`;
    const encodedSVG = encodeURIComponent(svg);

    const style = document.createElement("style");
    style.textContent = `.loop-widget-sp-selector-container::after { background-image: url("data:image/svg+xml;utf8,${encodedSVG}"); }`;

    const existingStyle = document.getElementById("loop-widget-svg-style");
    if (existingStyle) {
      existingStyle.remove();
    }

    style.id = "loop-widget-svg-style";
    document.head.appendChild(style);
  }

  function handleLoopWidgetSellingPlanGroupChange(productId, groupId, event) {
    if (
      event &&
      event?.target &&
      typeof event.target?.className === "string" &&
      event.target?.className?.includes("loop-widget-sp-selector")
    ) {
      return;
    }
    window.LOOP_WIDGET[productId]["selectedSellingPlanGroupId"] = groupId;
    const variantSellingPlans = loopWidgetGetVariantSpgSellingPlans(
      productId,
      groupId
    );
    uncheckLoopWidgetSellingPlans(productId);
    removeLoopWidgetSpgSelected(productId);
    handleLoopWidgetSellingPlanValue(productId, variantSellingPlans[0], true);
    const radio = document.getElementById(
      `loop-widget-purchase-option-radio-id-${groupId}`
    );
    if (radio) {
      radio.innerHTML = getLoopWidgetSvgRadio();
    }

    const container = document
      .getElementById(`loop-widget-container-id-${productId}`)
      ?.querySelector(`#loop-widget-purchase-option-id-${groupId}`);
    if (container) {
      container.classList.add("loop-widget-purchase-option-selected");
    }

    if (
      !getLoopWidgetPreferenceByKey(productId, "alwaysShowSellingPlanDetails")
    ) {
      document
        .querySelectorAll(`#loop-widget-container-id-${productId} .expanded`)
        .forEach((ele) => ele.classList.remove("expanded"));
      setTimeout(() => {
        const spgContainer = container.querySelector(
          ".loop-widget-spg-container"
        );
        if (spgContainer) {
          spgContainer.classList.add("expanded");
        }
      }, 50);
    }

    loopWidgetUpdateAddToCartButtonText(productId);
    updateSelectSVG();
  }

  function handleLoopWidgetSellingPlanChange(
    event,
    productId,
    sellingPlanGroupId
  ) {
    event.stopPropagation();
    const sellingPlanId = event.target.value;
    window.LOOP_WIDGET[productId]["selectedSellingPlanId"] = sellingPlanId;
    handleLoopWidgetSellingPlanValue(productId, sellingPlanId, true);
    changeLoopWidgetSellingPlanGroupPrice(
      sellingPlanId,
      productId,
      sellingPlanGroupId
    );
    changeLoopWidgetSellingPlanGroupDiscount(
      sellingPlanId,
      productId,
      sellingPlanGroupId
    );
    changeLoopWidgetSellingPlanGroupDescription(
      productId,
      sellingPlanGroupId,
      sellingPlanId
    );
  }

  function changeLoopWidgetSellingPlanGroupPrice(
    sellingPlanId,
    productId,
    sellingPlanGroupId
  ) {
    const storeJson = window.LOOP_WIDGET[productId]["storeJson"];
    const variant = getLoopWidgetVariantById(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const sellingPlan = variant.selling_plan_allocations.find(
      (a) => a.selling_plan_id == sellingPlanId
    );
    const { deliveryFreq, isPrepaid } =
      getLoopWidgetPrepaidSellingPlanDeliveryFreq(
        productId,
        sellingPlan.selling_plan_id
      );
    const value = storeJson.presetBundleShopifyProductIds?.includes(productId)
      ? getLoopWidgetBundlePriceBySellingPlanId(
          productId,
          sellingPlanId,
          false
        ) || sellingPlan.price / deliveryFreq
      : sellingPlan.price / deliveryFreq;
    document.getElementById(
      `loop-widget-purchase-option-price-id-${sellingPlanGroupId}`
    ).innerHTML = loopWidgetFormatPrice(value);
  }

  function changeLoopWidgetSellingPlanGroupDiscount(
    sellingPlanId,
    productId,
    sellingPlanGroupId
  ) {
    const variant = getLoopWidgetVariantById(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const sellingPlanGroup = window.LOOP_WIDGET[productId][
      "product"
    ].selling_plan_groups.find((g) => g.id === sellingPlanGroupId);
    const sellingPlan = sellingPlanGroup.selling_plans.find(
      (p) => p.id == sellingPlanId
    );
    const { discount, discountText } = getLoopWidgetDiscount(
      productId,
      sellingPlan,
      variant
    );
    const discEle = document.getElementById(
      `loop-widget-purchase-option-discount-badge-id-${sellingPlanGroupId}`
    );
    if (discEle) {
      discEle.innerHTML = `${getLoopWidgetTextsByKey(
        productId,
        "discountBadgeText"
      ).replace("{{discount_value}}", `${discountText}`)}`;
      if (discount > 0) {
        discEle.classList.remove("loop-display-none");
      } else {
        discEle.classList.add("loop-display-none");
      }
    }
  }

  function changeLoopWidgetSellingPlanGroupDescription(
    productId,
    sellingPlanGroupId,
    sellingPlanId
  ) {
    const sellingPlanGroup = window.LOOP_WIDGET[productId][
      "product"
    ].selling_plan_groups.find((g) => g.id === sellingPlanGroupId);
    const sellingPlan = sellingPlanGroup.selling_plans.find(
      (p) => p.id === Number(sellingPlanId)
    );
    const description = sellingPlan?.description || "";
    const descriptionEle = document.getElementById(
      `loop-widget-sp-selector-description-id-${sellingPlanGroupId}`
    );
    if (descriptionEle) {
      descriptionEle.innerHTML = description;
    }
  }

  function uncheckLoopWidgetSellingPlans(productId) {
    document
      .getElementById(`loop-widget-container-id-${productId}`)
      .querySelectorAll(".loop-widget-purchase-option-radio")
      .forEach((spg) => {
        spg.innerHTML = getLoopWidgetSvgRadio();
      });
  }

  function loopWidgetGetVariantSpgSellingPlans(productId, sellingPlanGroupId) {
    const variantToSellingPlans =
      window.LOOP_WIDGET[productId].variantToSellingPlans;
    return (
      variantToSellingPlans[getLoopWidgetVariantId(productId)][
        sellingPlanGroupId
      ] ?? []
    );
  }

  function generateLoopWidgetVariantSPMaps(productId) {
    const variantToSellingPlanGroups = {};
    const variantToSellingPlans = {};
    const sellingPlanGroupToSellingPlans = {};

    window.LOOP_WIDGET[productId]["product"].variants.forEach((variant) => {
      variantToSellingPlanGroups[variant.id] = [];
      variantToSellingPlans[variant.id] = {};

      variant.selling_plan_allocations.forEach((allocation) => {
        if (
          !variantToSellingPlanGroups[variant.id].includes(
            allocation.selling_plan_group_id
          )
        ) {
          variantToSellingPlanGroups[variant.id].push(
            allocation.selling_plan_group_id
          );
        }
        if (
          !variantToSellingPlans[variant.id][allocation.selling_plan_group_id]
        ) {
          variantToSellingPlans[variant.id][allocation.selling_plan_group_id] =
            [];
        }
        variantToSellingPlans[variant.id][
          allocation.selling_plan_group_id
        ].push(allocation.selling_plan_id);
        if (!sellingPlanGroupToSellingPlans[allocation.selling_plan_group_id]) {
          sellingPlanGroupToSellingPlans[allocation.selling_plan_group_id] = [];
        }
        if (
          !sellingPlanGroupToSellingPlans[
            allocation.selling_plan_group_id
          ].includes(allocation.selling_plan_id)
        ) {
          sellingPlanGroupToSellingPlans[allocation.selling_plan_group_id].push(
            allocation.selling_plan_id
          );
        }
      });
    });

    window.LOOP_WIDGET[productId] = {
      ...window.LOOP_WIDGET[productId],
      variantToSellingPlanGroups,
      variantToSellingPlans,
      sellingPlanGroupToSellingPlans,
    };
  }

  function loopWidgetHasCommonElements(arr1, arr2) {
    const set1 = new Set(arr1);
    return arr2.some((elem) => set1.has(elem));
  }

  function getVariantSellingPlanGroups(productId, variantId) {
    const storeJson = window.LOOP_WIDGET[productId]["storeJson"];
    const variantSellingPlanGroups =
      window.LOOP_WIDGET[productId]["variantToSellingPlanGroups"][variantId];
    const widgetSellingPlansToExclude =
      storeJson.shopifySellingPlanIdsToExcludeOnWidget ?? [];
    let sellingPlanGroups = window.LOOP_WIDGET[productId][
      "product"
    ].selling_plan_groups.filter((g) => {
      if (variantSellingPlanGroups.includes(g.id) && g.app_id === LOOP_APP_ID) {
        const sellingPlans =
          window.LOOP_WIDGET[productId]["sellingPlanGroupToSellingPlans"][g.id];
        if (
          !loopWidgetHasCommonElements(
            sellingPlans,
            widgetSellingPlansToExclude
          )
        ) {
          return g;
        }
      }
    });
    if (
      storeJson?.preferences?.hideBundleSellingPlansOnProductPage &&
      !storeJson.presetBundleShopifyProductIds.includes(productId)
    ) {
      sellingPlanGroups = sellingPlanGroups.filter((spg) => {
        const sellingPlanGroupSellingPlans =
          window.LOOP_WIDGET[productId]["sellingPlanGroupToSellingPlans"][
            spg.id
          ];
        const isBundleSellingPlanGroup = loopWidgetHasCommonElements(
          sellingPlanGroupSellingPlans,
          storeJson.bundleShopifySellingPlanIds
        );
        if (!isBundleSellingPlanGroup) {
          return spg;
        }
      });
    }
    return sellingPlanGroups;
  }

  function loopWidgetUpdateAddToCartButtonText(productId) {
    const buttons = getLoopWidgetAddToCartButtons(productId);
    console.log(buttons);
    const variant = getLoopWidgetVariantById(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const isAvailable = variant.available;
    const isSubscriptionSelected =
      window.LOOP_WIDGET[productId]["selectedSellingPlanId"];
    const buttonText = isAvailable
      ? isSubscriptionSelected
        ? getLoopWidgetTextsByKey(productId, "addSubscriptionToCartButtonText")
        : getLoopWidgetTextsByKey(productId, "addToCartButtonText")
      : getLoopWidgetTextsByKey(productId, "outOfStockText");
    buttons.forEach((button) => {
      updateButtonInnerHTMLLoopWidget(button, buttonText);
    });
  }

  function getLoopWidgetAddToCartButtons(productId) {
    if (window.LOOP_WIDGET[productId]["addToCartButtons"]?.length) {
      return window.LOOP_WIDGET[productId]["addToCartButtons"];
    }

    const loopWidgetContainer = document.querySelector(
      `#loop-widget-container-id-${productId}`
    );
    let buttons = [];
    const selectors = [
      'button[type="submit"][name="add"]',
      'button[type="submit"]',
      'button[name="add"]',
      "button[data-add-to-cart], button[data-ajax-add-to-cart]",
    ];

    if (loopWidgetContainer) {
      let commonAncestor = loopWidgetContainer.parentNode;
      let suitableAncestorFound = false;

      while (
        commonAncestor &&
        commonAncestor !== document.body &&
        !suitableAncestorFound
      ) {
        const forms = getLoopWidgetProductForms(productId, commonAncestor);
        forms.forEach((form) => {
          if (form && form.innerHTML.includes(productId)) {
            selectors.some((selector) => {
              const foundButtons = form.querySelectorAll(selector);
              if (foundButtons.length > 0) {
                buttons = [...buttons, ...foundButtons];
                suitableAncestorFound = true;
                return true;
              }
              return false;
            });
          }
        });

        if (!suitableAncestorFound) {
          commonAncestor = commonAncestor.parentNode;
        }
      }
    }

    if (buttons.length) {
      window.LOOP_WIDGET[productId]["addToCartButtons"] = buttons;
    }

    return buttons;
  }

  function updateButtonInnerHTMLLoopWidget(button, text) {
    if (button.firstElementChild) {
      button.firstElementChild.innerHTML = text;
    } else {
      button.innerHTML = text;
    }
  }

  function hideOneTimePurchaseOptionLoopWidget(productId) {
    const onetimePurchaseOption = document
      .getElementById(`loop-widget-container-id-${productId}`)
      ?.querySelector(`.loop-widget-purchase-option-onetime`);
    if (onetimePurchaseOption) {
      onetimePurchaseOption.classList.add("loop-display-none");
    }
  }

  function disableAddToCartBtnLoopWidget(productId) {
    getLoopWidgetAddToCartButtons(productId).forEach((btn) => {
      btn.disabled = true;
    });
  }

  function enableAddToCartBtnLoopWidget(productId) {
    getLoopWidgetAddToCartButtons(productId).forEach((btn) => {
      btn.disabled = false;
    });
  }

  // Functions to generate HTML
  function generateLoopWidgetTooltip(productId) {
    return getLoopWidgetPreferenceByKey(
      productId,
      "showSubscriptionDetailsPopup"
    )
      ? `
            <div class="loop-widget-tooltip-container">
                <div class="loop-widget-tooltip-header">
                    <div class="loop-widget-tooltip-image">
                        <svg width="15" height="15" viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg" class="loop-widget-tooltip-svg">
                            <path d="M45 0C20.1827 0 0 20.1827 0 45C0 69.8173 20.1827 90 45 90C69.8173 90 90 69.8174 90 45C90.0056 44.6025 89.9322 44.2078 89.7839 43.8389C89.6357 43.47 89.4156 43.1342 89.1365 42.8511C88.8573 42.568 88.5247 42.3432 88.158 42.1897C87.7912 42.0363 87.3976 41.9573 87 41.9573C86.6024 41.9573 86.2088 42.0363 85.842 42.1897C85.4753 42.3432 85.1427 42.568 84.8635 42.8511C84.5844 43.1342 84.3643 43.47 84.2161 43.8389C84.0678 44.2078 83.9944 44.6025 84 45C84 66.5748 66.5747 84 45 84C23.4253 84 6 66.5747 6 45C6 23.4254 23.4253 6 45 6C56.1538 6 66.3012 10.5882 73.4375 18H65.4062C65.0087 17.9944 64.614 18.0678 64.2451 18.2161C63.8762 18.3643 63.5405 18.5844 63.2573 18.8635C62.9742 19.1427 62.7494 19.4753 62.596 19.842C62.4425 20.2088 62.3635 20.6024 62.3635 21C62.3635 21.3976 62.4425 21.7912 62.596 22.158C62.7494 22.5247 62.9742 22.8573 63.2573 23.1365C63.5405 23.4156 63.8762 23.6357 64.2451 23.7839C64.614 23.9322 65.0087 24.0056 65.4062 24H79.8125C80.6081 23.9999 81.3711 23.6838 81.9337 23.1212C82.4963 22.5586 82.8124 21.7956 82.8125 21V6.59375C82.821 6.18925 82.7476 5.78722 82.5966 5.41183C82.4457 5.03644 82.2205 4.69545 81.9344 4.40936C81.6483 4.12327 81.3073 3.898 80.9319 3.7471C80.5565 3.5962 80.1545 3.52277 79.75 3.53125C79.356 3.53941 78.9675 3.62511 78.6067 3.78344C78.2458 3.94177 77.9197 4.16963 77.6469 4.45402C77.3741 4.73841 77.16 5.07375 77.0168 5.44089C76.8737 5.80803 76.8042 6.19977 76.8125 6.59375V12.875C68.6156 4.86282 57.3081 0 45 0ZM43.75 20.75C43.356 20.7582 42.9675 20.8439 42.6067 21.0022C42.2458 21.1605 41.9197 21.3884 41.6469 21.6728C41.3741 21.9572 41.16 22.2925 41.0168 22.6596C40.8737 23.0268 40.8042 23.4185 40.8125 23.8125V47.375C40.8116 47.7693 40.8883 48.16 41.0385 48.5246C41.1886 48.8892 41.4092 49.2207 41.6875 49.5L54.0938 61.9375C54.6573 62.5011 55.4217 62.8177 56.2188 62.8177C57.0158 62.8177 57.7802 62.5011 58.3438 61.9375C58.9073 61.3739 59.224 60.6095 59.224 59.8125C59.224 59.0155 58.9073 58.2511 58.3438 57.6875L46.8125 46.1875V23.8125C46.821 23.408 46.7476 23.006 46.5966 22.6306C46.4457 22.2552 46.2205 21.9142 45.9344 21.6281C45.6483 21.342 45.3073 21.1168 44.9319 20.9658C44.5565 20.8149 44.1545 20.7415 43.75 20.75Z"></path>
                        </svg>
                    </div>
                    <div class="loop-widget-tooltip-label">${getLoopWidgetTextsByKey(
                      productId,
                      "subscriptionDetailsText"
                    )}</div>
                </div>
                <div class="loop-widget-tooltip-text">
                    <div class="loop-widget-container-arrow"></div>
                    <div>${getLoopWidgetTextsByKey(
                      productId,
                      "subscriptionDetailsDescription"
                    )}</div>
                </div>
            </div>`
      : "";
  }

  function generateLoopWidgetVariantPriceTemplateOnetime(productId, variant) {
    const storeJson = window.LOOP_WIDGET[productId]["storeJson"];
    const originalPrice = LOOP_WIDGET_USE_COMPARE_AT_PRICE
      ? variant.compare_at_price
      : variant.price;
    const price = storeJson.presetBundleShopifyProductIds.includes(productId)
      ? getLoopWidgetBundlePriceBySellingPlanId(productId, null, true) ||
        originalPrice
      : originalPrice;
    return `
            <div class="loop-widget-purchase-option-price-each-container">
                <div class="loop-widget-purchase-option-price-container">
                ${
                  getLoopWidgetPreferenceByKey(productId, "showCompareAtPrice")
                    ? `<div class="loop-widget-purchase-option-compare-at-price loop-display-none">${loopWidgetFormatPrice(
                        originalPrice
                      )}</div>`
                    : ""
                }
                <div id="loop-widget-purchase-option-price-onetime-${productId}" class="loop-widget-purchase-option-price">${loopWidgetFormatPrice(
      price
    )}</div>
                </div>
                ${
                  !getLoopWidgetPreferenceByKey(productId, "hideEachLabel")
                    ? `<div class="loop-widget-purchase-option-each-label">${getLoopWidgetTextsByKey(
                        productId,
                        "priceLabelText"
                      )}</div>`
                    : ""
                }
            </div>`;
  }

  function generateLoopWidgetOnetimeContainer(productId, variantId) {
    const onetimeContainerClass = [
      "loop-widget-purchase-option",
      getLoopWidgetPreferenceByKey(productId, "purchaseOptionsOrder") !==
        "Display one-time purchase first" &&
        "loop-widget-purchase-option-border-top",
      "loop-widget-purchase-option-onetime",
    ]
      .filter(Boolean)
      .join(" ");
    const variant = getLoopWidgetVariantById(productId, variantId);
    const variantSellingPlanGroups = getVariantSellingPlanGroups(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const requires_selling_plan =
      window.LOOP_WIDGET[productId].product.requires_selling_plan;
    const radioContainer =
      getLoopWidgetPreferenceByKey(
        productId,
        "hideRadioButtonIfOnePlanAvailable"
      ) &&
      variantSellingPlanGroups.length === 1 &&
      requires_selling_plan
        ? ""
        : `<div id="loop-widget-onetime-purchase-option-radio-id-${productId}" class="loop-widget-purchase-option-radio">
                    ${getLoopWidgetSvgRadio()}
                </div>`;

    return `
            <div data-loop-widget-onetime-purchase-option data-loop-widget-purchase-option class="${onetimeContainerClass}" onclick="window.LoopSubscriptions.handleLoopWidgetOnetimeClick(${productId})">
                <div class="loop-widget-purchase-option-header">
                    <div class="loop-widget-spg-label-discount-wrapper">
                        ${radioContainer}
                        <label for="loop-widget-onetime-purchase-option-radio-id-${productId}" class="loop-widget-purchase-option-label">${getLoopWidgetTextsByKey(
      productId,
      "oneTimePurchaseLabel"
    )}</label>
                        <div id="loop-widget-onetime-purchase-option-discount-badge-id-${productId}" class="loop-widget-purchase-option-discount-badge loop-display-none"></div>
                    </div>
                    ${generateLoopWidgetVariantPriceTemplateOnetime(
                      productId,
                      variant
                    )}
                </div>
            </div>`;
  }

  function generateLoopWidgetSellingPlan(sp) {
    return `<option class="loop-widget-sp-option" value="${sp.id}">${
      sp.options[0].value ?? sp.name
    }</option>`;
  }

  function generateLoopWidgetPlanSelector(
    productId,
    sellingPlanGroup,
    sellingPlanGroupId,
    selling_plans
  ) {
    if (
      getLoopWidgetPreferenceByKey(
        productId,
        "hidePlanSelectorIfOnePlanAvailable"
      ) &&
      selling_plans.length === 1
    ) {
      return "";
    }
    const label = sellingPlanGroup?.options?.[0]?.name ?? "";

    if (
      getLoopWidgetPreferenceByKey(
        productId,
        "showPlanSelectorAsTextIfOnePlanAvailable"
      ) &&
      selling_plans.length === 1
    ) {
      return `<div class="loop-widget-sp-selector-wrapper">
                <div class="loop-widget-sp-selector-label-as-text"><span class="loop-widget-sp-selector-as-text-label">${label}: </span><span class="loop-widget-sp-option">${selling_plans[0].name}</span></div>
            </div>`;
    }

    return `
          <div class="loop-widget-sp-selector-wrapper">
            <label class="loop-widget-sp-selector-label">${label}</label>
            <div class="loop-widget-sp-selector-container">
              <select class="loop-widget-sp-selector" name="selling_plan" onchange="window.LoopSubscriptions.handleLoopWidgetSellingPlanChange(event, ${productId}, '${sellingPlanGroupId}')">
                ${selling_plans.map(generateLoopWidgetSellingPlan).join("")}
              </select>
            </div>
          </div>`;
  }

  function generateLoopWidgetSellingPlanDescription(
    sellingPlanGroupId,
    sellingPlanGroup,
    selling_plans
  ) {
    const description = selling_plans[0].description || "";
    return `
            <div class="loop-widget-sp-selector-description-wrapper">
                <div id="loop-widget-sp-selector-description-id-${sellingPlanGroupId}" class="loop-widget-sp-selector-description">${description}</div>
            </div>`;
  }

  function generateLoopWidgetSellingPlanSelector(
    productId,
    sellingPlanGroup,
    sellingPlanGroupId,
    selling_plans
  ) {
    const variantSellingPlans = loopWidgetGetVariantSpgSellingPlans(
      productId,
      sellingPlanGroupId
    );
    const filteredSPforVariant = selling_plans.filter((sp) =>
      variantSellingPlans.includes(sp.id)
    );
    const requires_selling_plan =
      window.LOOP_WIDGET[productId].product.requires_selling_plan;

    return `
            <div id="loop-widget-spg-container-id-${sellingPlanGroupId}" class="${
      getLoopWidgetPreferenceByKey(
        productId,
        "hideRadioButtonIfOnePlanAvailable"
      ) && requires_selling_plan
        ? "loop-widget-spg-container loop-widget-left-margin-0"
        : "loop-widget-spg-container"
    }">
                ${generateLoopWidgetPlanSelector(
                  productId,
                  sellingPlanGroup,
                  sellingPlanGroupId,
                  filteredSPforVariant
                )}
                ${generateLoopWidgetSellingPlanDescription(
                  sellingPlanGroupId,
                  sellingPlanGroup,
                  filteredSPforVariant
                )}
            </div>`;
  }

  function generateLoopWidgetPurchaseOptionPriceContainer(
    productId,
    sellingPlanGroupId
  ) {
    const storeJson = window.LOOP_WIDGET[productId]["storeJson"];
    const variant = getLoopWidgetVariantById(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const sellingPlans = loopWidgetGetVariantSpgSellingPlans(
      productId,
      sellingPlanGroupId
    );
    const sellingPlan = variant.selling_plan_allocations.find(
      (a) => a.selling_plan_id === sellingPlans[0]
    );
    const { deliveryFreq, isPrepaid } =
      getLoopWidgetPrepaidSellingPlanDeliveryFreq(
        productId,
        sellingPlan.selling_plan_id
      );
    const originalPrice = LOOP_WIDGET_USE_COMPARE_AT_PRICE
      ? variant.compare_at_price
      : variant.price;
    const price = storeJson.presetBundleShopifyProductIds.includes(productId)
      ? getLoopWidgetBundlePriceBySellingPlanId(
          productId,
          sellingPlan.selling_plan_id,
          false
        ) || sellingPlan.price / deliveryFreq
      : sellingPlan.price / deliveryFreq;

    return `
            <div class="loop-widget-purchase-option-price-each-container">
                <div class="loop-widget-purchase-option-price-container">
                    ${
                      originalPrice === price ||
                      !getLoopWidgetPreferenceByKey(
                        productId,
                        "showCompareAtPrice"
                      )
                        ? ""
                        : `<div class="loop-widget-purchase-option-compare-at-price">${loopWidgetFormatPrice(
                            originalPrice
                          )}</div>`
                    }
                    <div id="loop-widget-purchase-option-price-id-${sellingPlanGroupId}" class="loop-widget-purchase-option-price">${loopWidgetFormatPrice(
      price
    )}</div>
                </div>
                ${
                  !getLoopWidgetPreferenceByKey(productId, "hideEachLabel")
                    ? `<div class="loop-widget-purchase-option-each-label">${getLoopWidgetTextsByKey(
                        productId,
                        "priceLabelText"
                      )}</div>`
                    : ""
                }
            </div>`;
  }

  function generateLoopWidgetPurchaseOptionDiscountBadge(
    productId,
    sellingPlanGroupId
  ) {
    const variant = getLoopWidgetVariantById(
      productId,
      getLoopWidgetVariantId(productId)
    );
    const variantSellingPlans = loopWidgetGetVariantSpgSellingPlans(
      productId,
      sellingPlanGroupId
    );
    const firstSellingPlan = window.LOOP_WIDGET[productId][
      "product"
    ].selling_plan_groups
      .find((g) => g.id === sellingPlanGroupId)
      ["selling_plans"].find((sp) => sp.id === variantSellingPlans[0]);
    const { discount, discountText } = getLoopWidgetDiscount(
      productId,
      firstSellingPlan,
      variant
    );
    const discText = getLoopWidgetTextsByKey(
      productId,
      "discountBadgeText"
    )?.replace("{{discount_value}}", `${discountText}`);
    return getLoopWidgetPreferenceByKey(
      productId,
      "showDiscountBadgeForSubscription"
    )
      ? `<div id="loop-widget-purchase-option-discount-badge-id-${sellingPlanGroupId}" class="loop-widget-purchase-option-discount-badge${
          discount > 0 ? "" : " loop-display-none"
        }">${discText}</div>`
      : "";
  }

  function generateLoopWidgetLabel(id, name) {
    return `<label for="loop-widget-purchase-option-radio-id-${id}" class="loop-widget-purchase-option-label">${name}</label>`;
  }

  function getLoopWidgetSvgRadio() {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="loop-widget-radio-svg">
                <circle cx="12" cy="12" r="10" stroke="var(--loop-widget-purchase-option-radio-accent-color)" stroke-width="2"/>
                <circle cx="12" cy="12" r="6" fill="var(--loop-widget-purchase-option-radio-accent-color)"/>
            </svg>`;
  }

  function setSvgDimensions(productId) {
    const svgList = document
      .getElementById(`loop-widget-container-id-${productId}`)
      .querySelectorAll(".loop-widget-radio-svg");
    svgList.forEach((svg) => {
      const width = getComputedStyle(document.documentElement).getPropertyValue(
        "--loop-widget-purchase-option-radio-width"
      );
      const height = getComputedStyle(
        document.documentElement
      ).getPropertyValue("--loop-widget-purchase-option-radio-height");
      svg.setAttribute("width", width.trim());
      svg.setAttribute("height", height.trim());
    });
  }

  function updateLoopWidgetDropdownArrowSVG() {
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue("--loop-widget-dropdown-arrow-svg-stroke-color")
      .trim();
    const height = getComputedStyle(document.documentElement)
      .getPropertyValue("--loop-widget-dropdown-arrow-svg-height")
      .trim();
    const width = getComputedStyle(document.documentElement)
      .getPropertyValue("--loop-widget-dropdown-arrow-svg-width")
      .trim();

    const finalColor = color || "#000";
    const finalHeight = height || "30px";
    const finalWidth = width || "30px";

    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${finalWidth}' height='${finalHeight}' viewBox='0 0 24 24' fill='none'><path d='M17 9.5L12 14.5L7 9.5' stroke='${finalColor}' stroke-linecap='round' stroke-linejoin='round'/></svg>`;
    const encodedSVG = encodeURIComponent(svg);

    const styleContent = `.loop-widget-sp-selector-container::after { background-image: url("data:image/svg+xml;utf8,${encodedSVG}"); background-size: contain; }`;
    let style = document.getElementById("loop-widget-dropdown-arrow-svg");
    if (!style) {
      style = document.createElement("style");
      style.id = "loop-widget-dropdown-arrow-svg";
      document.body.appendChild(style);
    }
    style.textContent = styleContent;
  }

  function generateLoopWidgetRadioInput(
    productId,
    sellingPlanGroupId,
    selectedSellingPlanGroupId,
    spgLength
  ) {
    const requires_selling_plan =
      window.LOOP_WIDGET[productId].product.requires_selling_plan;
    if (
      getLoopWidgetPreferenceByKey(
        productId,
        "hideRadioButtonIfOnePlanAvailable"
      ) &&
      spgLength === 1 &&
      requires_selling_plan
    ) {
      return "";
    }
    const radioHtml = getLoopWidgetSvgRadio();

    return `
            <div id="loop-widget-purchase-option-radio-id-${sellingPlanGroupId}" class="loop-widget-purchase-option-radio">
                ${radioHtml}
            </div>
        `;
  }

  function generateLoopWidgetSellingPlanContainer(productId, variantId) {
    let availableSellingPlanGroups = getVariantSellingPlanGroups(
      productId,
      variantId
    );
    const selectedSellingPlanGroupId =
      getLoopWidgetSelectedSellingPlanGroupId(productId);
    if (availableSellingPlanGroups.length === 0) {
      hideLoopWidget(
        productId,
        "generateLoopWidgetSellingPlanContainer",
        "availableSellingPlanGroups length is 0"
      );
    } else {
      showLoopWidget(productId);
    }

    return availableSellingPlanGroups
      .map(
        (sellingPlanGroup) => `
            <div data-loop-widget-selling-plan-group data-loop-widget-purchase-option id="loop-widget-purchase-option-id-${
              sellingPlanGroup.id
            }" class="loop-widget-purchase-option" onclick="window.LoopSubscriptions.handleLoopWidgetSellingPlanGroupChange(${productId},'${
          sellingPlanGroup.id
        }', event)">
                <div class="loop-widget-purchase-option-header">
                    <div class="loop-widget-spg-label-discount-wrapper">
                        ${generateLoopWidgetRadioInput(
                          productId,
                          sellingPlanGroup.id,
                          selectedSellingPlanGroupId,
                          availableSellingPlanGroups.length
                        )}
                        ${generateLoopWidgetLabel(
                          sellingPlanGroup.id,
                          sellingPlanGroup.name
                        )}
                        ${generateLoopWidgetPurchaseOptionDiscountBadge(
                          productId,
                          sellingPlanGroup.id
                        )}
                    </div>
                    ${generateLoopWidgetPurchaseOptionPriceContainer(
                      productId,
                      sellingPlanGroup.id
                    )}
                </div>
                ${
                  sellingPlanGroup.selling_plans.length > 0
                    ? generateLoopWidgetSellingPlanSelector(
                        productId,
                        sellingPlanGroup,
                        sellingPlanGroup.id,
                        sellingPlanGroup.selling_plans
                      )
                    : ""
                }
            </div>`
      )
      .join(" ");
  }

  function generateLoopWidgetPurchaseOptions(productId, variantId) {
    return `
            <div class="loop-widget-purchase-options-container">
                ${
                  getLoopWidgetPreferenceByKey(
                    productId,
                    "purchaseOptionsOrder"
                  ) === "Display one-time purchase first"
                    ? generateLoopWidgetOnetimeContainer(productId, variantId)
                    : ""
                }
                ${generateLoopWidgetSellingPlanContainer(productId, variantId)}
                ${
                  getLoopWidgetPreferenceByKey(
                    productId,
                    "purchaseOptionsOrder"
                  ) !== "Display one-time purchase first"
                    ? generateLoopWidgetOnetimeContainer(productId, variantId)
                    : ""
                }
            </div>`;
  }

  function generateLoopWidgetPurchaseOptionsLabel(productId) {
    return getLoopWidgetPreferenceByKey(productId, "showPurchaseOptionsLabel")
      ? `<div class="loop-widget-purchase-options-label">${getLoopWidgetTextsByKey(
          productId,
          "purchaseOptionLabel"
        )}</div>`
      : "";
  }

  function generateLoopWidget(productId, variantId) {
    const requires_selling_plan =
      window.LOOP_WIDGET[productId].product.requires_selling_plan;
    if (
      requires_selling_plan &&
      getLoopWidgetPreferenceByKey(productId, "hideWidgetIfOnePlanAvailable") &&
      window.LOOP_WIDGET[productId].product.selling_plan_groups.length === 1
    ) {
      hideLoopWidget(
        productId,
        "generateLoopWidget",
        "hideWidgetIfOnePlanAvailable and selling plan groups length is 1"
      );
      return "";
    }

    return `
            ${generateLoopWidgetPurchaseOptionsLabel(productId)}
            ${generateLoopWidgetPurchaseOptions(productId, variantId)}
            ${generateLoopWidgetTooltip(productId)}`;
  }

  /** Loop bundle functions */

  async function loopWidgetProcessBundleProduct(productId) {
    const storeJSON = window.LOOP_WIDGET[productId].storeJson;
    if (
      !storeJSON.hasPresetBundles ||
      !storeJSON.presetBundleShopifyProductIds.includes(productId)
    ) {
      return;
    }
    const productBundleData = getLoopWidgetProductBundleData(productId);
    if (productBundleData && productBundleData.variants.length) {
      loopWidgetOverrideAddToCartButton(productId);
      handleLoopBundleWidgetVisibility(productId);
      loopWidgetCheckAllowCheckoutIfBundle(productId);
    }
  }

  function getLoopWidgetProductBundleData(productId) {
    return window.LOOP_WIDGET[productId]?.productBundleData ?? {};
  }

  function getLoopWidgetBundleVariantInfo(productId, variantId) {
    const productBundleData = getLoopWidgetProductBundleData(productId);
    return productBundleData.variants.find((v) => v.shopifyId === variantId);
  }

  function getLoopWidgetBundlePriceBySellingPlanId(
    productId,
    sellingPlanId,
    isOneTime
  ) {
    const selectedVariantId = getLoopWidgetVariantId(productId);
    const bundleVariant = getLoopWidgetBundleVariantInfo(
      productId,
      selectedVariantId
    );
    const variant = getLoopWidgetVariantById(productId, selectedVariantId);
    if (!variant || !bundleVariant) return 0;

    const conversionRate = window.Shopify.currency.rate;
    const bundleDiscountedPriceOnetime =
      bundleVariant.oneTimePrice * 100 * conversionRate;
    const bundleDiscountedPriceSubscription =
      bundleVariant.sellingPlanPrices[sellingPlanId] * 100 * conversionRate;

    return isOneTime
      ? bundleDiscountedPriceOnetime
      : bundleDiscountedPriceSubscription;
  }

  function handleLoopBundleWidgetVisibility(productId) {
    const purchaseType = getLoopWidgetProductBundleData(productId).purchaseType;
    if (purchaseType === "SUBSCRIPTION") {
      hideOneTimePurchaseOptionLoopWidget(productId);
    } else if (purchaseType === "ONETIME") {
      hideLoopWidget(
        productId,
        "handleLoopBundleWidgetVisibility",
        "Bundle is one time available only"
      );
    }
  }

  function loopWidgetCheckAllowCheckoutIfBundle(productId) {
    enableAddToCartBtnLoopWidget(productId);
    const selectedVariantId = getLoopWidgetVariantId(productId);
    const selectedVariant = getLoopWidgetBundleVariantInfo(
      productId,
      selectedVariantId
    );

    if (selectedVariant?.outOfStock) {
      const buttonText = getLoopWidgetTextsByKey(productId, "outOfStockText");
      const addToCartButtons = getLoopWidgetAddToCartButtons(productId);
      if (addToCartButtons.length) {
        addToCartButtons.forEach((addToCartButton) => {
          setTimeout(
            () => updateButtonInnerHTMLLoopWidget(addToCartButton, buttonText),
            500
          );
        });
      }
      disableAddToCartBtnLoopWidget(productId);
    }
  }

  function listenLoopWidgetAddToCartCustomEvent() {
    document.addEventListener(
      "loopPresetAddToCartSuccessEvent",
      function (event) {
        const { productId, response } = event.detail;
        window.location.href = "/cart";
        widgetLogger(
          `Loop Product ${productId} added to cart. Response: ${JSON.stringify(
            response
          )}`
        );
      }
    );
  }

  function loopWidgetRemoveAllEventListenersForEle(element) {
    const clone = element.cloneNode(true);
    element.parentNode.replaceChild(clone, element);
    return clone;
  }

  function loopWidgetGetQuantity(productId) {
    for (const selector of LOOP_WIDGET_PRODUCT_QUANTITY_SELECTORS) {
      const quantityEle = document.querySelector(selector);
      if (quantityEle) {
        return quantityEle.value;
      }
    }
  }

  function loopWidgetOverrideAddToCartButton(productId) {
    getLoopWidgetAddToCartButtons(productId).forEach((btn) => {
      btn = loopWidgetRemoveAllEventListenersForEle(btn);
      btn.addEventListener("click", (event) => {
        const quantity = loopWidgetGetQuantity(productId);
        loopWidgetHandleAddToCartEvent(event, productId, quantity);
      });
    });
    window.LOOP_WIDGET[productId]["addToCartButtons"] = [];
  }

  async function loopWidgetCreateBundleTransaction(productId, payload) {
    try {
      const authorization =
        window.LOOP_WIDGET[productId].storeJson.sentinalAuthToken;
      const response = await fetch(
        `${window.LOOP_WIDGET[productId].storeJson.apiUrl.bundleTransaction}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authorization,
          },
          body: JSON.stringify(payload),
        }
      );
      const responseJson = await response.json();
      return responseJson.data.transactionId;
    } catch (error) {
      widgetLogger("loopWidgetCreateBundleTransaction", error);
      throw error;
    }
  }

  function loopWidgetCreateBundleTransactionPayload(
    productId,
    quantity,
    selectedSellingPlanId
  ) {
    const selectedVariantId = getLoopWidgetVariantId(productId);
    const bundleVariant = getLoopWidgetBundleVariantInfo(
      productId,
      selectedVariantId
    );
    if (!bundleVariant) return { payload: null, bundleVariantDiscount: null };

    const discount = selectedSellingPlanId
      ? bundleVariant.mappedDiscounts.find(
          (d) => d.purchaseType === "SUBSCRIPTION"
        )
      : bundleVariant.mappedDiscounts.find(
          (d) => d.purchaseType !== "SUBSCRIPTION"
        );

    if (!discount) return { payload: null, bundleVariantDiscount: null };

    return {
      payload: {
        presetProductShopifyId: Number(productId),
        presetDiscountId: discount.id,
        presetVariantShopifyId: Number(selectedVariantId),
        totalQuantity: Number(quantity),
        sellingPlanShopifyId: Number(selectedSellingPlanId),
      },
      bundleVariantDiscount: discount,
    };
  }

  async function handleBundleTransactionLoopWidget(
    productId,
    quantity,
    selectedSellingPlanId
  ) {
    try {
      const { payload, bundleVariantDiscount } =
        loopWidgetCreateBundleTransactionPayload(
          productId,
          quantity,
          selectedSellingPlanId
        );
      if (!payload)
        return { bundleTransactionId: null, bundleVariantDiscount: null };

      const bundleTransactionId = await loopWidgetCreateBundleTransaction(
        productId,
        payload
      );
      if (!bundleTransactionId) enableAddToCartBtnLoopWidget(productId);
      return { bundleTransactionId, bundleVariantDiscount };
    } catch (error) {
      enableAddToCartBtnLoopWidget(productId);
      widgetLogger("handleBundleTransactionLoopWidget", error);
      return { bundleTransactionId: null, bundleVariantDiscount: null };
    }
  }

  async function getLoopWidgetBundleDiscountAttributes() {
    try {
      const url = `https://${
        window.Shopify.cdnHost.split("/cdn")[0]
      }/cart.json`;
      const res = await (await fetch(url)).json();
      const loopBundleDiscountAttributes = res.attributes
        ?._loopBundleDiscountAttributes
        ? JSON.parse(res.attributes._loopBundleDiscountAttributes)
        : {};

      const bundleIdsInCart = new Set(
        res.items.map((item) => item.properties?._bundleId).filter(Boolean)
      );

      return Object.keys(loopBundleDiscountAttributes)
        .filter((key) => bundleIdsInCart.has(key))
        .reduce((obj, key) => {
          obj[key] = loopBundleDiscountAttributes[key];
          return obj;
        }, {});
    } catch (error) {
      widgetLogger("getLoopWidgetBundleDiscountAttributes", error);
      return {};
    }
  }

  async function loopWidgetCreateAddToCartPayload(
    productId,
    bundleTransactionId,
    bundleVariantDiscount,
    selectedSellingPlanId,
    selectedBundleVariantId,
    quantity,
    productBundleData
  ) {
    const formData = {
      items: [],
      attributes: {
        _loopBundleDiscountAttributes: {},
      },
    };

    const oldAttr = await getLoopWidgetBundleDiscountAttributes();
    const currentDiscountAttribute = {
      [bundleTransactionId]: {
        discountType: bundleVariantDiscount.type,
        discountValue: bundleVariantDiscount.value,
        discountComputedValue: bundleVariantDiscount
          ? selectedSellingPlanId
            ? bundleVariantDiscount.sellingPlanComputedDiscounts[
                selectedSellingPlanId
              ] * quantity
            : bundleVariantDiscount.oneTimeDiscount * quantity
          : 0,
      },
    };

    formData.attributes._loopBundleDiscountAttributes = JSON.stringify({
      ...oldAttr,
      ...currentDiscountAttribute,
    });

    const selectedBundleVariant = getLoopWidgetBundleVariantInfo(
      productId,
      selectedBundleVariantId
    );
    const selectedBundleVariantProducts =
      selectedBundleVariant?.mappedProductVariants ?? [];

    if (selectedBundleVariantProducts.length) {
      selectedBundleVariantProducts.forEach((childProduct) => {
        const obj = {
          id: childProduct.shopifyId,
          quantity: childProduct.quantity * quantity,
          selling_plan: selectedSellingPlanId,
          properties: {
            _bundleId: bundleTransactionId,
            _isPresetBundleProduct: true,
            ...(window.LOOP_WIDGET[productId].storeJson.preferences
              .showBundleName
              ? { bundleName: productBundleData.name ?? "" }
              : { _bundleName: productBundleData.name ?? "" }),
          },
        };
        formData.items.push(obj);
      });
    }

    return formData;
  }

  async function shopifyAddToCartByLoopWidget(payload, productId) {
    const endpoint = `${window.Shopify.routes.root}cart/add.js`;
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      await dispatchLoopWidgetEvent(
        productId,
        "loopPresetAddToCartSuccessEvent",
        data
      );
    } catch (error) {
      widgetLogger("shopifyAddToCartByLoopWidget", error);
      enableAddToCartBtnLoopWidget(productId);
    }
  }

  async function loopWidgetHandleAddToCartEvent(
    event,
    productId,
    quantity = 1
  ) {
    event.preventDefault();
    event.stopPropagation();
    disableAddToCartBtnLoopWidget(productId);

    const bundleVariant = getLoopWidgetBundleVariantInfo(
      productId,
      getLoopWidgetVariantId(productId)
    );
    if (!bundleVariant || bundleVariant.outOfStock) {
      return;
    }

    const productBundleData = getLoopWidgetProductBundleData(productId);
    const selectedSellingPlanId =
      window.LOOP_WIDGET[productId].selectedSellingPlanId;

    const { bundleTransactionId, bundleVariantDiscount } =
      await handleBundleTransactionLoopWidget(
        productId,
        quantity,
        selectedSellingPlanId
      );
    if (!bundleTransactionId) {
      return;
    }

    const payload = await loopWidgetCreateAddToCartPayload(
      productId,
      bundleTransactionId,
      bundleVariantDiscount,
      selectedSellingPlanId,
      getLoopWidgetVariantId(productId),
      quantity,
      productBundleData
    );
    await shopifyAddToCartByLoopWidget(payload, productId);
  }

  async function dispatchLoopWidgetEvent(productId, eventName, response) {
    const addToCartEvent = new CustomEvent(eventName, {
      detail: { productId, response },
    });
    document.dispatchEvent(addToCartEvent);
  }

  return {
    startLoopWidget,
    handleLoopWidgetOnetimeClick,
    handleLoopWidgetSellingPlanGroupChange,
    handleLoopWidgetSellingPlanChange,
    handleLoopWidgetVariantIdChange,
  };
})();
